/** Dependencies */
import angular from 'angular';
/** Styles */
import './gridPagination.scss';
/** Template */
import gridPaginationTemplate from './gridPagination.template.html';

/*******************************************************************************
 * gridPagination
 *
 * Pager for server-side paginated grids: Prev/Next buttons, a "jump to page"
 * dropdown and the total match count. Place it below the results grid.
 *
 * How to use it, in two steps:
 *
 * 1. Your search function takes the page number, sends it to the server and
 *    saves the server's response on the controller:
 *
 *      $ctrl.search = function (page) {
 *        $ctrl.filters.page = page || 1;
 *        $ctrl.filters.pageSize = 100;
 *
 *        return MySvc.findThings($ctrl.filters).then(function (resp) {
 *          $ctrl.grid.data = resp.data.receipts;
 *          $ctrl.pagination = resp.data;  // <-- feed the response to the pager
 *        });
 *      };
 *
 * 2. Point the component at that saved response and your search function:
 *
 *      <grid-pagination
 *        pagination="$ctrl.pagination"
 *        label="Receipts"
 *        on-page-change="$ctrl.search(page)"
 *      ></grid-pagination>
 *
 * That's it. When the user clicks Prev/Next or picks a page from the
 * dropdown, the component calls your search function with the page number
 * they asked for. It never calls it with a page that doesn't exist, so your
 * search does not need any guard logic of its own.
 *
 * The component is always visible. Until a search has found something (ex.
 * before the first search runs, or after a Clear) the controls are simply
 * disabled and the counts show 0.
 *******************************************************************************/
export default angular
  .module('csm.ui.gridPagination', [])
  .component('gridPagination', {
    bindings: {
      /** The paging info from the last search. Pass the find response data
       *  directly; the component reads page, totalPages, pageSize and
       *  totalCount (or totalReceiptCount, the name the receipt find
       *  endpoints use) off of it and ignores everything else.
       *  Null/undefined zeroes the counts and disables the controls. */
      pagination: '<',
      /** Noun for the total count label, ex. "Receipts". Defaults to
       *  "Records". */
      label: '@?',
      /** Optionally disables the controls, ex. while a fetch is in flight. */
      disabled: '<?',
      /** The search to run for the requested page:
       *  on-page-change="search(page)". Only called with valid, in-range
       *  page numbers. */
      onPageChange: '&'
    },
    template: gridPaginationTemplate,
    controller: GridPaginationController
  }).name;

/*******************************************************************************
 * GridPaginationController
 *
 * The component only renders the controls and reports which page the user
 * asked for; the owner runs the actual search and feeds the response back in
 * through the pagination binding. That keeps this component free of any
 * request/promise logic, so there is only one place data can come from.
 *******************************************************************************/
/* @ngInject */ function GridPaginationController() {
  const $ctrl = this;

  Object.assign($ctrl, {
    /** Current page number (1-based), read from the pagination binding. */
    page: 1,
    /** Total number of pages, read from the pagination binding. */
    totalPages: 0,
    /** Total number of matches, read from the pagination binding. */
    totalCount: 0,
    /** Position of the page's first and last row within the full result
     *  set, formatted for display in the "Showing X-Y of Z" readout. */
    rangeStart: '0',
    rangeEnd: '0',
    /** Options for the "jump to page" dropdown: [1, 2, ... totalPages]. */
    pageOptions: [],
    /** ng-model for the dropdown. Kept separate from page so a selection
     *  that gets rejected doesn't leave the real state out of sync. Blank
     *  until a search has loaded some pages to pick from. */
    selectedPage: null,

    $onChanges: $onChanges,
    goToPage: goToPage
  });

  /***************************************************************************
   * $onChanges */
  /**
   * Runs every time the owner assigns a new pagination object (angular
   * calls this automatically when a one-way binding gets a new value).
   * Copies the paging info into the fields the template displays.
   ***************************************************************************/
  function $onChanges(changes) {
    if (!changes.pagination) return;

    // No search results yet (or the owner cleared them). The zeroed counts
    // keep the controls disabled via the ng-disabled in the template.
    const info = $ctrl.pagination || {};

    $ctrl.page = info.page || 1;
    $ctrl.totalPages = info.totalPages || 0;
    $ctrl.totalCount =
      info.totalCount != null ? info.totalCount : info.totalReceiptCount || 0;
    $ctrl.pageOptions = Array.from(
      { length: $ctrl.totalPages },
      (_, i) => i + 1
    );

    /**
     * "Showing X-Y" range for the current page. The last page is usually
     * shorter than pageSize, so the end is capped at the total.
     *
     * Caveat: the range is the page's POSITION within the full result set,
     * not a count of the rows on screen. Those are almost always the same,
     * but a screen that drops rows after the search (ex. Return Receipts
     * filters out fully returned receipts) can show slightly fewer rows
     * than the range implies. That is expected, not a paging bug.
     */
    const pageSize = info.pageSize || 0;
    $ctrl.rangeStart = (($ctrl.page - 1) * pageSize + 1).toLocaleString();
    $ctrl.rangeEnd = Math.min(
      $ctrl.page * pageSize,
      $ctrl.totalCount
    ).toLocaleString();

    // With no pages there is nothing to select, so leave the dropdown blank
    // instead of pointing it at an option that does not exist.
    $ctrl.selectedPage = $ctrl.totalPages ? $ctrl.page : null;
  }

  /***************************************************************************
   * goToPage */
  /**
   * Asks the owner to search the requested page. Ignores pages outside of
   * the available range (ex. stale totalPages during a refetch) so the
   * owner's search function never has to check the page number itself.
   *
   * @param  page  Page number to request.
   ***************************************************************************/
  function goToPage(page) {
    if ($ctrl.disabled || page < 1 || page > $ctrl.totalPages) {
      // Put the dropdown back on the real page for a rejected selection.
      $ctrl.selectedPage = $ctrl.page;
      return;
    }

    $ctrl.onPageChange({ page: page });
  }
}
